<template>

    <div id="open-ticket">

        <v-overlay :value="loading">
            <v-progress-circular indeterminate></v-progress-circular>
        </v-overlay>

        <v-form ref="form">

            <!-- Step 1: Domain -->
            <div>
                <v-select
                    :items="domains"
                    :label="$t('SELECT_A_DOMAIN_LABEL')"
                    v-model="ticket.domain"
                    :rules="[v => !!v || $t('WARNING_DOMAIN_REQUIRED')]"
                    required
                    density="compact"
                    :disabled="step > 1 || loading || requestInProgress"
                ></v-select>
                <div class="step-navigation">
                    <v-btn v-if="step === 1" @click="nextStep" elevation="1">{{ $t('NEXT_STEP') }}</v-btn>
                    <v-btn v-if="step === 1" @click="prevStep" variant="text">{{ $t('PREVIOUS_STEP') }}</v-btn>
                </div>
            </div>

            <!-- Step 2: Topic -->
            <div v-if="step >= 2">
                <v-select
                    :items="helpTopics"
                    :item-title="item => item.topic"
                    :item-value="item => item.topicId"
                    :label="$t('SELECT_A_TOPIC_LABEL')"
                    :placeholder="$t('SELECT_A_TOPIC_PLACEHOLDER')"
                    v-model="ticket.topicId"
                    :rules="[v => !!v || $t('WARNING_TOPIC_REQUIRED')]"
                    required
                    density="compact"
                    :disabled="step > 2 || loading || requestInProgress"
                ></v-select>
                <div class="step-navigation">
                    <v-btn v-if="step === 2" @click="nextStep" elevation="1">{{ $t('NEXT_STEP') }}</v-btn>
                    <v-btn v-if="step === 2" @click="prevStep" variant="text">{{ $t('PREVIOUS_STEP') }}</v-btn>
                </div>
            </div>

            <!-- Step 3: Subject -->
            <div v-if="step >= 3">
                <v-text-field
                    :label="$t('TICKET_SUBJECT_INPUT_LABEL')"
                    v-model="ticket.subject"
                    :rules="[v => !!v || $t('WARNING_SUBJECT_REQUIRED')]"
                    required
                    density="compact"
                    :disabled="step > 3 || loading || requestInProgress"
                ></v-text-field>
                <div class="step-navigation">
                    <v-btn v-if="step === 3" @click="nextStep" elevation="1">{{ $t('NEXT_STEP') }}</v-btn>
                    <v-btn v-if="step === 3" @click="prevStep" variant="text">{{ $t('PREVIOUS_STEP') }}</v-btn>
                </div>
            </div>

            <!-- Step 4: Message -->
            <div v-if="step >= 4">
                <v-textarea
                    :label="$t('TICKET_MESSAGE_INPUT_LABEL')"
                    v-model="ticket.message"
                    :rules="[v => !!v || $t('WARNING_MESSAGE_REQUIRED')]"
                    required
                    density="compact"
                ></v-textarea>
                <div class="step-navigation">
                    <v-btn @click="submitForm" :disabled="!valid || requestInProgress" :loading="requestInProgress" elevation="1">{{ $t('SUBMIT_BUTTON_TEXT') }}</v-btn>
                    <v-btn v-if="step === 4" @click="prevStep" :disabled="requestInProgress" variant="text">{{ $t('PREVIOUS_STEP') }}</v-btn>
                </div>
            </div>

        </v-form>

    </div>

</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
    name: "EscalationForm",
    props: {
        escalationOption: {
            type: String,
            required: true
        },
        callbackFunction: {
            type: Function,
            required: true
        }
    },
    data() {
        return {
            loading: true,
            inProgress: false,
            ticket: {
                domain: '',
                topicId: 0,
                subject: '',
                message: '',
            },
            step: 1,
            valid: false,
        };
    },
    async created() {
        this.loading = true;
        this.setInProgress(true);

        if (!this.getPublisherDomainList.isLoaded) {
            await this.getPublisherDomainList();
        }

        if (!this.ticketHelpTopicList.isLoaded) {
            await this.getTicketHelpTopicList();
        }

        this.loading = false;
        this.setInProgress(false);
    },
    computed: {
        ...mapGetters(["publisherDomainList", "ticketHelpTopicList"]),
        requestInProgress() {
            return this.inProgress;
        },
        domains() {
            return this.publisherDomainList.list.map(domain => domain.domain);
        },
        helpTopics() {
            switch (this.escalationOption) {
                case this.$const.ESCALATION_OPTION__LIVECHAT:
                    return [];
                case this.$const.ESCALATION_OPTION__TICKET:
                    return [
                        {topicId: 0, topic: this.$t('SELECT_A_TOPIC_PLACEHOLDER')},
                        ...this.ticketHelpTopicList.list,
                    ];
            }

            return [];
        }
    },
    methods: {
        ...mapActions(["getPublisherDomainList", "getTicketHelpTopicList"]),
        async getHelpTopicList(escalationOption) {   
            if (escalationOption === this.$const.ESCALATION_OPTION__LIVECHAT) {
                // await SupportService.getLiveChatTopics(); // future task
            } else {
                await this.getTicketHelpTopicList();
            }            
        },    
        async submitForm() {
            if (this.$refs.form.validate()) {

                this.setInProgress(true);

                const formData = this.ticket
                formData.chatId = this.$store.state.chat.chatId;

                await this.callbackFunction(formData);

                this.setInProgress(false);
            }
        },
        nextStep() {
            switch (this.step) {
                case 1:
                    if (this.ticket.domain) this.step++;
                    break;
                case 2:
                    if (this.ticket.topicId) this.step++;
                    break;
                case 3:
                    if (this.ticket.subject) this.step++;
                    break;
            }
        },
        prevStep() {
            if (this.step > 1) {
                this.step--;
            } else {
                this.$emit('exitForm');
            }
        },
        scrollToBottom() {
            const scrollToPosition = (document.body.scrollHeight + 1000) || (document.documentElement.scrollHeight + 1000);
            window.scrollTo(0, scrollToPosition);

        },
        setInProgress(inProgress) {
            this.inProgress = inProgress;
        },
    },
    watch: {
        ticket: {
            handler() {
                this.valid = this.$refs.form.validate();
            },
            deep: true,
        },
        step: {
            handler() {
                this.$nextTick(() => {
                    this.scrollToBottom();
                });
            },
            deep: true,
        }
    },
};
</script>